import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link as RouterLink } from 'react-router-dom';
import sectionStyles from '../../StructuredSections.module.scss';
import { ProcessedSection } from '../../sectionProcessing';
import { PIMCORE_CONTENT_BASE_URL } from '../../../../../utils/Pimcore';
import type {
    ResourceLibrarySitePageImageBlock,
    ResourceLibrarySitePageLink,
} from '../../../../../graphql/pimcore/modules/resourceLibrary/types/resourceLibrarySitePageItems';
import { resolveSitePageLink } from '../SitePageLinkResolver';
import type { ResolvedSitePageLink } from '../SitePageLinkResolver';

const IMAGE_GALLERY_COLUMNS_DESKTOP = 2;
const galleryColumnsStyle = {
    '--image-gallery-columns': String(IMAGE_GALLERY_COLUMNS_DESKTOP),
} as React.CSSProperties;

interface GalleryItem {
    thumbnailSrc: string;
    fullSrc: string;
    title: string | null;
    caption: string | null;
    alt: string;
    link: ResourceLibrarySitePageLink | null;
}

interface GalleryEntry {
    item: GalleryItem;
    resolvedLink: ResolvedSitePageLink | null;
    lightboxIndex: number | null;
}

interface SitePageImageGalleryProps {
    blocks: ResourceLibrarySitePageImageBlock[];
}

interface GalleryComputation {
    entries: GalleryEntry[];
    lightboxItems: GalleryItem[];
}

export function renderImageBlockContent(section: ProcessedSection): React.ReactNode {
    if (!section.imageBlocks || section.imageBlocks.length === 0) {
        return null;
    }

    return <SitePageImageGallery blocks={section.imageBlocks} />;
}

function useImageGalleryLightbox(blocks: ResourceLibrarySitePageImageBlock[]): {
    galleryEntries: GalleryEntry[];
    lightboxItems: GalleryItem[];
    activeLightboxIndex: number | null;
    isLightboxOpen: boolean;
    openLightboxForGalleryIndex: (galleryIndex: number) => void;
    closeLightbox: () => void;
    showPrevious: () => void;
    showNext: () => void;
    setActiveLightboxIndex: React.Dispatch<React.SetStateAction<number | null>>;
} {
    const currentOrigin = typeof window !== 'undefined' ? window.location.origin : undefined;

    const { entries, lightboxItems } = useMemo<GalleryComputation>(() => {
        const computedEntries: GalleryEntry[] = [];
        const lightbox: GalleryItem[] = [];

        blocks.forEach((block) => {
            const item: GalleryItem = {
                thumbnailSrc: `${PIMCORE_CONTENT_BASE_URL}${block.thumbnailUrl}`,
                fullSrc: `${PIMCORE_CONTENT_BASE_URL}${block.imageUrl}`,
                title: block.imageTitle ?? null,
                caption: block.imageCaption ?? null,
                alt: block.imageAltText ?? '',
                link: block.imageLink ?? null,
            };

            const resolvedLink = item.link ? resolveSitePageLink(item.link, currentOrigin) : null;
            let lightboxIndex: number | null = null;

            if (!item.link) {
                lightboxIndex = lightbox.length;
                lightbox.push(item);
            }

            computedEntries.push({
                item,
                resolvedLink,
                lightboxIndex,
            });
        });

        return {
            entries: computedEntries,
            lightboxItems: lightbox,
        };
    }, [blocks, currentOrigin]);

    const [activeLightboxIndex, setActiveLightboxIndex] = useState<number | null>(null);

    const openLightboxForGalleryIndex = useCallback(
        (galleryIndex: number) => {
            const entry = entries[galleryIndex];
            if (!entry || entry.lightboxIndex === null) {
                return;
            }
            setActiveLightboxIndex(entry.lightboxIndex);
        },
        [entries]
    );

    const closeLightbox = useCallback(() => {
        setActiveLightboxIndex(null);
    }, []);

    const showPrevious = useCallback(() => {
        setActiveLightboxIndex((current) => {
            if (current === null || current === 0) {
                return current;
            }
            return current - 1;
        });
    }, []);

    const showNext = useCallback(() => {
        setActiveLightboxIndex((current) => {
            if (current === null || current >= lightboxItems.length - 1) {
                return current;
            }
            return current + 1;
        });
    }, [lightboxItems.length]);

    return {
        galleryEntries: entries,
        lightboxItems,
        activeLightboxIndex,
        isLightboxOpen: activeLightboxIndex !== null,
        openLightboxForGalleryIndex,
        closeLightbox,
        showPrevious,
        showNext,
        setActiveLightboxIndex,
    };
}

interface ImageGalleryGridProps {
    entries: GalleryEntry[];
    onOpenLightbox: (galleryIndex: number) => void;
}

const ImageGalleryGrid: React.FC<ImageGalleryGridProps> = ({ entries, onOpenLightbox }) => (
    <div className={sectionStyles.imageGalleryGrid} style={galleryColumnsStyle}>
        {entries.map((entry, index) => {
            const { item, resolvedLink, lightboxIndex } = entry;
            const hasCaption = Boolean(item.caption);
            const galleryCaptionId = hasCaption
                ? `site-page-image-gallery-caption-${index}`
                : undefined;
            const linkTarget = item.link?.openInNewTab ? '_blank' : undefined;
            const linkRel = item.link?.openInNewTab ? 'noopener noreferrer' : undefined;

            const imageNode = (
                <img
                    className={sectionStyles.imageGalleryImage}
                    src={item.thumbnailSrc}
                    alt={item.alt}
                    aria-describedby={galleryCaptionId}
                />
            );

            let interactiveNode: React.ReactNode;

            if (resolvedLink && resolvedLink.href) {
                if (resolvedLink.isExternal) {
                    interactiveNode = (
                        <a
                            className={sectionStyles.imageGalleryButton}
                            href={resolvedLink.href}
                            target={linkTarget}
                            rel={linkRel}
                        >
                            {imageNode}
                        </a>
                    );
                } else {
                    interactiveNode = (
                        <RouterLink
                            className={sectionStyles.imageGalleryButton}
                            to={resolvedLink.href}
                            target={linkTarget}
                            rel={linkRel}
                        >
                            {imageNode}
                        </RouterLink>
                    );
                }
            } else if (lightboxIndex !== null) {
                interactiveNode = (
                    <button
                        type="button"
                        className={sectionStyles.imageGalleryButton}
                        onClick={() => onOpenLightbox(index)}
                    >
                        {imageNode}
                    </button>
                );
            } else {
                interactiveNode = imageNode;
            }

            return (
                <figure className={sectionStyles.imageGalleryItem} key={`image-block-${index}`}>
                    {interactiveNode}
                    {hasCaption && (
                        <figcaption
                            className={sectionStyles.imageGalleryCaption}
                            id={galleryCaptionId}
                        >
                            {item.caption && (
                                <span className={sectionStyles.groupCaption}>{item.caption}</span>
                            )}
                        </figcaption>
                    )}
                </figure>
            );
        })}
    </div>
);

interface ImageGalleryLightboxProps {
    item: GalleryItem | null;
    isOpen: boolean;
    activeIndex: number | null;
    totalItems: number;
    onClose: () => void;
    onPrevious: () => void;
    onNext: () => void;
    closeButtonRef: React.MutableRefObject<HTMLButtonElement | null>;
}

const ImageGalleryLightbox: React.FC<ImageGalleryLightboxProps> = ({
    item,
    isOpen,
    activeIndex,
    totalItems,
    onClose,
    onPrevious,
    onNext,
    closeButtonRef,
}) => {
    if (!isOpen || activeIndex === null || !item) {
        return null;
    }

    const headerTitle = item.title ?? '';
    const headerCaption = item.caption ?? '';
    const titleId = headerTitle ? `site-page-image-lightbox-title-${activeIndex}` : undefined;
    const captionId = headerCaption ? `site-page-image-lightbox-caption-${activeIndex}` : undefined;
    const describedBy = [titleId, captionId].filter(Boolean).join(' ') || undefined;
    const showPreviousArrow = totalItems > 1 && activeIndex > 0;
    const showNextArrow = totalItems > 1 && activeIndex < totalItems - 1;

    return createPortal(
        <div
            className={sectionStyles.imageLightboxOverlay}
            role="dialog"
            aria-modal="true"
            onClick={onClose}
        >
            <div
                className={sectionStyles.imageLightboxContent}
                role="presentation"
                onClick={(event) => event.stopPropagation()}
            >
                <header className={sectionStyles.imageLightboxHeader}>
                    <span className={sectionStyles.imageLightboxCounter}>
                        {activeIndex + 1}/{totalItems}
                    </span>
                    {(headerTitle || headerCaption) && (
                        <div className={sectionStyles.imageLightboxHeaderText}>
                            {headerTitle && (
                                <span className={sectionStyles.imageLightboxTitle} id={titleId}>
                                    {headerTitle}
                                </span>
                            )}
                            {headerCaption && (
                                <span className={sectionStyles.imageLightboxCaption} id={captionId}>
                                    {headerCaption}
                                </span>
                            )}
                        </div>
                    )}
                    <button
                        ref={closeButtonRef}
                        type="button"
                        className={sectionStyles.imageLightboxCloseButton}
                        onClick={onClose}
                        aria-label="Close gallery"
                    >
                        &times;
                    </button>
                </header>
                <div className={sectionStyles.imageLightboxBody}>
                    <figure className={sectionStyles.imageLightboxFigure}>
                        {showPreviousArrow && (
                            <button
                                type="button"
                                className={`${sectionStyles.imageLightboxArrow} ${sectionStyles.imageLightboxArrowLeft}`}
                                onClick={onPrevious}
                                aria-label="Show previous image"
                            >
                                &larr;
                            </button>
                        )}
                        <img
                            className={sectionStyles.imageLightboxFigureImage}
                            src={item.fullSrc}
                            alt={item.alt}
                            aria-describedby={describedBy}
                        />
                        {showNextArrow && (
                            <button
                                type="button"
                                className={`${sectionStyles.imageLightboxArrow} ${sectionStyles.imageLightboxArrowRight}`}
                                onClick={onNext}
                                aria-label="Show next image"
                            >
                                &rarr;
                            </button>
                        )}
                    </figure>
                </div>
            </div>
        </div>,
        document.body
    );
};

const SitePageImageGallery: React.FC<SitePageImageGalleryProps> = ({ blocks }) => {
    const {
        galleryEntries,
        lightboxItems,
        activeLightboxIndex,
        isLightboxOpen,
        openLightboxForGalleryIndex,
        closeLightbox,
        showPrevious,
        showNext,
        setActiveLightboxIndex,
    } = useImageGalleryLightbox(blocks);

    const closeButtonRef = useRef<HTMLButtonElement | null>(null);

    useEffect(() => {
        if (activeLightboxIndex === null) {
            return;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [activeLightboxIndex]);

    useEffect(() => {
        if (activeLightboxIndex === null || !closeButtonRef.current) {
            return;
        }

        closeButtonRef.current.focus();
    }, [activeLightboxIndex]);

    useEffect(() => {
        if (activeLightboxIndex === null) {
            return;
        }

        function handleKeyDown(event: KeyboardEvent) {
            if (event.key === 'Escape') {
                event.preventDefault();
                closeLightbox();
                return;
            }

            if (event.key === 'ArrowLeft' || event.key === 'Backspace') {
                event.preventDefault();
                showPrevious();
                return;
            }

            if (event.key === 'ArrowRight' || event.key === ' ') {
                event.preventDefault();
                showNext();
            }
            if (event.key === 'Home') {
                event.preventDefault();
                setActiveLightboxIndex(0);
            }
            if (event.key === 'End') {
                event.preventDefault();
                setActiveLightboxIndex(lightboxItems.length - 1);
            }
        }

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [
        activeLightboxIndex,
        lightboxItems.length,
        closeLightbox,
        showNext,
        showPrevious,
        setActiveLightboxIndex,
    ]);

    const lightboxItem =
        activeLightboxIndex !== null ? lightboxItems[activeLightboxIndex] ?? null : null;

    const gallerySectionClassName = isLightboxOpen
        ? `${sectionStyles.imageGallerySection} ${sectionStyles.imageGallerySectionLightboxActive}`
        : sectionStyles.imageGallerySection;

    return (
        <div className={gallerySectionClassName}>
            <ImageGalleryGrid
                entries={galleryEntries}
                onOpenLightbox={openLightboxForGalleryIndex}
            />
            <ImageGalleryLightbox
                item={lightboxItem}
                isOpen={isLightboxOpen}
                activeIndex={activeLightboxIndex}
                totalItems={lightboxItems.length}
                onClose={closeLightbox}
                onPrevious={showPrevious}
                onNext={showNext}
                closeButtonRef={closeButtonRef}
            />
        </div>
    );
};
